if (!customElements.get('page-judgeme-reviews')) {
  customElements.define('page-judgeme-reviews', class extends HTMLElement {
    connectedCallback() {
      this.pageSize = Math.max(1, Number(this.dataset.reviewsPerPage || 3));
      this.minimumRating = Math.max(1, Math.min(5, Number(this.dataset.minRating || 5)));
      this.verifiedLabel = this.dataset.verifiedLabel || 'Verified buyer';
      this.loadLabel = this.dataset.loadMoreLabel || 'Load More';
      this.fewerLabel = this.dataset.showFewerLabel || 'Show Fewer Reviews';
      this.emptyText = this.dataset.emptyText || 'Store reviews are not available right now.';
      this.errorText = this.dataset.errorText || 'Reviews could not be loaded. Please try again.';
      this.showMedia = this.dataset.showMedia !== 'false';
      this.grid = this.querySelector('[data-review-grid]');
      this.status = this.querySelector('[data-review-status]');
      this.moreButton = this.querySelector('[data-load-more]');
      this.writeButton = this.querySelector('[data-write-review]');
      this.native = this.querySelector('[data-native-source]');
      this.summary = this.querySelector('[data-review-summary]');
      this.averageNode = this.querySelector('[data-average-rating]');
      this.countNode = this.querySelector('[data-review-count]');
      this.reviews = [];
      this.visibleCount = this.pageSize;

      this.moreButton?.addEventListener('click', () => this.toggleReviews());
      this.writeButton?.addEventListener('click', () => this.openReviewPage());
      this.init();
    }

    async init() {
      this.setLoading(true, this.dataset.loadingText || 'Loading our best customer reviews…');

      try {
        const reviews = await this.waitForNativeReviews();
        this.reviews = this.rankReviews(this.unique(reviews));
        this.visibleCount = Math.min(this.pageSize, this.reviews.length);
        this.render();
        this.updateSummary();
        this.updateButton();
        this.setLoading(false);
      } catch (error) {
        console.warn('[Store-wide Judge.me reviews]', error);
        this.reviews = [];
        this.render();
        this.updateSummary();
        this.updateButton();
        this.setLoading(false);
        this.showStatus(this.errorText);
      }
    }

    async waitForNativeReviews() {
      if (!this.native) return [];

      const read = () => this.parseNative(this.native);
      const immediate = read();
      if (immediate.length) return immediate;

      return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          const reviews = read();
          if (!reviews.length) return;
          observer.disconnect();
          resolve(reviews);
        });

        observer.observe(this.native, { childList: true, subtree: true, attributes: true });

        window.setTimeout(() => {
          observer.disconnect();
          resolve(read());
        }, 5000);
      });
    }

    parseNative(root) {
      const selectors = [
        '.jdgm-rev',
        '.jdgm-all-reviews__review',
        '[data-review-id]',
        '[class*="jdgm-rev"]'
      ];

      const nodes = [...new Set(selectors.flatMap((selector) => [...root.querySelectorAll(selector)]))]
        .filter((element) => element.querySelector('.jdgm-rev__body, .jdgm-rev__title, [class*="review-body"], [class*="review-title"]'));

      return nodes.map((element, index) => {
        const ratingNode = element.querySelector('[data-score], [data-rating], .jdgm-rev__rating, .jdgm-star');
        const image = element.querySelector('.jdgm-rev__pic-link img, .jdgm-rev__pics img, img[class*="review"]');

        return {
          id: String(element.dataset.reviewId || element.dataset.id || element.id || `store-review-${index}`),
          rating: this.clampRating(
            ratingNode?.dataset.score ||
            ratingNode?.dataset.rating ||
            ratingNode?.getAttribute('data-score') ||
            ratingNode?.getAttribute('data-rating') ||
            this.ratingFromStars(element)
          ),
          title: this.text(element, '.jdgm-rev__title, [class*="review-title"]'),
          body: this.text(element, '.jdgm-rev__body, [class*="review-body"]'),
          author: this.text(element, '.jdgm-rev__author, [class*="review-author"]') || 'Anonymous',
          verified: Boolean(element.querySelector('.jdgm-rev__buyer-badge, [class*="verified"], [class*="buyer-badge"]')),
          date: this.text(element, '.jdgm-rev__timestamp, time, [class*="review-date"]'),
          product: this.text(element, '.jdgm-rev__prod-link, [class*="product-title"], [class*="product-name"]'),
          image: image?.currentSrc || image?.src || '',
          sourceIndex: index
        };
      }).filter((review) => review.body || review.title);
    }

    ratingFromStars(element) {
      const stars = element.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-star[aria-label*="star"], [class*="star"][class*="filled"]');
      return stars.length || 5;
    }

    rankReviews(reviews) {
      const featured = reviews.filter((review) => review.rating >= this.minimumRating);
      const pool = featured.length >= this.pageSize ? featured : reviews;

      return [...pool].sort((a, b) => {
        const scoreA = this.score(a);
        const scoreB = this.score(b);
        return scoreB - scoreA || a.sourceIndex - b.sourceIndex;
      });
    }

    score(review) {
      let score = review.rating * 100;
      if (review.verified) score += 30;
      if (review.body.length >= 80) score += 16;
      if (review.body.length >= 160) score += 8;
      if (review.title) score += 8;
      if (review.image) score += 12;
      return score;
    }

    toggleReviews() {
      if (!this.reviews.length) return;
      this.visibleCount = this.visibleCount >= this.reviews.length
        ? Math.min(this.pageSize, this.reviews.length)
        : Math.min(this.visibleCount + this.pageSize, this.reviews.length);
      this.render();
      this.updateButton();
    }

    render() {
      if (!this.grid) return;

      const fragment = document.createDocumentFragment();
      this.reviews.slice(0, this.visibleCount).forEach((review) => fragment.append(this.card(review)));
      this.grid.replaceChildren(fragment);
      this.grid.hidden = !this.reviews.length;
      this.grid.setAttribute('aria-busy', 'false');
      this.showStatus(this.reviews.length ? '' : this.emptyText);
    }

    updateSummary() {
      const aggregate = this.readStoreAggregate();
      const parsedCount = this.reviews.length;
      const count = aggregate.count > 0 ? aggregate.count : parsedCount;

      if (!count) {
        if (this.summary) this.summary.hidden = true;
        return;
      }

      const parsedAverage = parsedCount
        ? this.reviews.reduce((sum, review) => sum + review.rating, 0) / parsedCount
        : 0;
      const average = aggregate.rating > 0 ? aggregate.rating : parsedAverage;

      if (this.averageNode) this.averageNode.textContent = average ? average.toFixed(1) : '5.0';
      if (this.countNode) {
        this.countNode.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB').format(count);
      }
      if (this.summary) this.summary.hidden = false;
    }

    readStoreAggregate() {
      if (!this.native) return { count: 0, rating: 0 };

      const countNode = this.native.querySelector(
        '[data-number-of-reviews], .jdgm-carousel-number-of-reviews, .jdgm-all-reviews-text__text, .jdgm-rev-widg__summary-text, .jdgm-all-reviews__summary-text'
      );
      const ratingNode = this.native.querySelector(
        '.jdgm-all-reviews-rating[data-score], .jdgm-all-reviews-text__text[data-score], .jdgm-rev-widg__summary [data-score], .jdgm-all-reviews__summary [data-score]'
      );

      let count = this.parseNumber(
        countNode?.dataset?.numberOfReviews ||
        countNode?.getAttribute?.('data-number-of-reviews')
      );
      let rating = this.parseNumber(
        ratingNode?.dataset?.score ||
        ratingNode?.getAttribute?.('data-score')
      );

      const summaryText = this.clean(countNode?.textContent);
      if (!count && summaryText) {
        const countMatch = summaryText.match(/([\d,.]+)\s+(?:verified\s+)?reviews?\b/i);
        count = this.parseNumber(countMatch?.[1]);
      }
      if (!rating && summaryText) {
        const ratingMatch = summaryText.match(/(\d(?:\.\d+)?)\s*(?:\/|out\s+of)\s*5/i);
        rating = this.parseNumber(ratingMatch?.[1]);
      }

      return {
        count: Math.max(0, Math.round(count || 0)),
        rating: rating > 0 && rating <= 5 ? rating : 0
      };
    }

    updateButton() {
      if (!this.moreButton) return;
      const hasMore = this.reviews.length > this.pageSize;
      this.moreButton.hidden = !hasMore;
      this.moreButton.textContent = this.visibleCount >= this.reviews.length ? this.fewerLabel : this.loadLabel;
    }

    card(review) {
      const article = document.createElement('article');
      article.className = 'pdp-judgeme-reviews__card';

      const header = document.createElement('div');
      header.className = 'pdp-judgeme-reviews__card-header';

      const stars = document.createElement('span');
      stars.className = 'pdp-judgeme-reviews__card-stars';
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      header.append(stars);

      if (review.verified) {
        const badge = document.createElement('span');
        badge.className = 'pdp-judgeme-reviews__verified';
        badge.textContent = this.verifiedLabel;
        header.append(badge);
      }

      article.append(header);

      if (review.title) {
        const title = document.createElement('h3');
        title.className = 'pdp-judgeme-reviews__card-title';
        title.textContent = review.title;
        article.append(title);
      }

      if (review.body) {
        const body = document.createElement('p');
        body.className = 'pdp-judgeme-reviews__card-body';
        body.textContent = `“${review.body}”`;
        article.append(body);
      }

      if (this.showMedia && review.image) {
        const link = document.createElement('a');
        link.className = 'pdp-judgeme-reviews__media';
        link.href = review.image;
        link.target = '_blank';
        link.rel = 'noopener';

        const image = document.createElement('img');
        image.src = review.image;
        image.alt = `Review photo from ${review.author}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
        article.append(link);
      }

      const footer = document.createElement('footer');
      footer.className = 'pdp-judgeme-reviews__card-footer';

      const reviewer = document.createElement('div');
      reviewer.className = 'pdp-judgeme-reviews__reviewer';

      const name = document.createElement('strong');
      name.textContent = review.author;
      reviewer.append(name);

      if (review.product) {
        const product = document.createElement('span');
        product.textContent = review.product;
        reviewer.append(product);
      }

      footer.append(reviewer);

      if (review.date) {
        const date = document.createElement('span');
        date.className = 'pdp-judgeme-reviews__date';
        date.textContent = review.date;
        footer.append(date);
      }

      article.append(footer);
      return article;
    }

    openReviewPage() {
      const nativeWriteLink = this.native?.querySelector('.jdgm-write-rev-link, [data-write-review], a[href*="review"]');
      if (nativeWriteLink?.href) {
        window.location.href = nativeWriteLink.href;
        return;
      }
      window.location.href = '/pages/judgeme_reviews';
    }

    setLoading(active, message = '') {
      this.grid?.setAttribute('aria-busy', String(active));
      if (active) this.showStatus(message);
      else if (this.reviews.length) this.showStatus('');
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    unique(reviews) {
      const seen = new Set();
      return reviews.filter((review) => {
        const key = this.clean(`${review.id}|${review.author}|${review.body || review.title}`).toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    text(root, selector) {
      const node = root.querySelector(selector);
      return this.clean(node?.textContent);
    }

    clean(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    parseNumber(value) {
      const normalized = String(value ?? '').replace(/,/g, '').trim();
      const number = Number.parseFloat(normalized);
      return Number.isFinite(number) ? number : 0;
    }

    clampRating(value) {
      return Math.max(1, Math.min(5, Math.round(Number(value) || 5)));
    }
  });
}
